/**
 * `/stammdaten` -- locations, lists (with reorder), and settings (including
 * the two self-serve feature switches).
 *
 * THREE SECTIONS ON ONE ROUTE, THE SECTION IS IN THE URL: the same shape as
 * `/mitarbeiter` (lib/router.ts's `parseMasterDataTab`/`masterDataTabHref`,
 * Task 9's m-6 decision carried forward -- a reload, a bookmark or a pasted
 * link must land back on the same section). Unlike the Staff view there is no
 * per-section VISIBILITY question here: every route this view touches is
 * `adminGuards('manager')` (api/src/adminRoutes.ts), so all three tabs are
 * always offered to anyone who reaches this route at all.
 *
 * DECIDES NOTHING ITSELF, and APPLIES nothing itself either. Every decision
 * (grouping, labels, error copy) and every mutation (`*Action`) lives in
 * lib/masterDataView.ts, where a Node test can hold the collaborators -- the
 * STANDING RULE from the Task 7-9 reviews. What is left here is JSX and
 * component state; the call sites a mutation could still point at the wrong
 * row -- a rename/delete on the wrong location or list, a drag that persists
 * the wrong order, a feature toggle that writes the wrong flag or value --
 * are asserted in DOM suite #1 (test/dom/layoutGating.test.tsx).
 */
import { useRef, useState } from 'react';
import type { DragEvent, FormEvent, MouseEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMe } from '../Boot';
import { useRoute } from '../Router';
import { ADMIN_API } from '../lib/api';
import { guardedAction } from '../lib/gate';
import { t } from '../lib/i18n';
import {
  isPlainLeftClick, MASTER_DATA_TABS, masterDataTabHref, parseMasterDataTab, pathFor,
} from '../lib/router';
import {
  changedSettings, clearLocationBarcodeAction, createListAction, createLocationAction,
  deleteListAction, deleteLocationAction, locationBarcodeLabel, masterDataTabLabel,
  renameListAction, renameLocationAction, reorderListsAction, saveSettingsAction,
  setLocationBarcodeAction, settingsFieldLabel, settingsFieldPlaceholder, settingsGroups,
  toggleSelfServeFeatureAction, type MutationShell, type SelfServeFeature,
} from '../lib/masterDataView';
import { formatDateTime } from '../lib/startView';
import { locationsQueryOptions, listsQueryOptions, settingsQueryOptions } from '../queries';
import { ErrorNotice } from '../components/ErrorNotice';
import { PageBody, PageHeader, TabNav } from '../components/ui/page';
import { Spinner } from '../components/Spinner';
import { StatusPill } from '../components/StatusPill';
import type { Features, ListEntry, Location, SettingsResponse } from '../lib/types';

/** The panel-level busy/error pair every mutation-bearing panel shares, plus
 *  the re-entrancy gate -- the same shape views/Staff.tsx uses.
 *
 *  ONE GATE PER PANEL, not one per view: each panel calls this hook for
 *  itself, so a rename on the Standorte table cannot block a save on the
 *  Einstellungen form. That matches `busy`, which was already per-panel. */
function useMutationShell(): MutationShell & {
  busy: boolean;
  error: string | null;
  gate: { current: string | null };
} {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const gate = useRef<string | null>(null);
  return { queryClient, setBusy, setError, busy, error, gate };
}

function PanelError({ message }: { message: string | null }) {
  if (message === null) return null;
  return (
    <p className="master-data-error" role="alert">
      {message}
    </p>
  );
}

// ── Standorte ────────────────────────────────────────────────────────────────

function LocationsPanel() {
  const shell = useMutationShell();
  const locations = useQuery(locationsQueryOptions());
  const [name, setName] = useState('');
  const [barcode, setBarcode] = useState('');
  const [renameTarget, setRenameTarget] = useState<Location | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [barcodeTarget, setBarcodeTarget] = useState<Location | null>(null);
  const [barcodeValue, setBarcodeValue] = useState('');

  // Every handler on this view is TIER 1 (lib/gate.ts): they all write. The
  // create pair is the plainest case -- a double-submit is two rows with the
  // same name, and only the second one's error would ever be visible.
  function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    guardedAction(shell.gate, 'createLocation', () => createLocationAction({ name, barcode }, {
      ...shell,
      createLocation: ADMIN_API.createLocation,
      onCreated: () => { setName(''); setBarcode(''); },
    }));
  }

  function openRename(row: Location) {
    setRenameTarget(row);
    setRenameValue(row.name);
    shell.setError(null);
  }
  function handleRenameSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (renameTarget === null) return;
    const target = renameTarget;
    guardedAction(shell.gate, 'updateLocation', () => renameLocationAction(target, renameValue, {
      ...shell,
      updateLocation: ADMIN_API.updateLocation,
      onRenamed: () => { setRenameTarget(null); setRenameValue(''); },
    }));
  }

  function openBarcodeDialog(row: Location) {
    setBarcodeTarget(row);
    // Deliberately EMPTY, never pre-filled with the row's current barcode: an
    // untouched field must not be indistinguishable from an emptied one
    // (lib/masterDataView.ts's docblock on `setLocationBarcodeAction`).
    setBarcodeValue('');
    shell.setError(null);
  }
  function handleBarcodeSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (barcodeTarget === null) return;
    const target = barcodeTarget;
    guardedAction(shell.gate, 'updateLocation', () => setLocationBarcodeAction(target, barcodeValue, {
      ...shell,
      updateLocation: ADMIN_API.updateLocation,
      onSaved: () => { setBarcodeTarget(null); setBarcodeValue(''); },
    }));
  }

  function handleClearBarcode(row: Location) {
    guardedAction(shell.gate, 'updateLocation', () => clearLocationBarcodeAction(
      row, { ...shell, updateLocation: ADMIN_API.updateLocation },
    ));
  }

  function handleDelete(row: Location) {
    guardedAction(shell.gate, 'deleteLocation', () => deleteLocationAction(row, {
      ...shell,
      deleteLocation: ADMIN_API.deleteLocation,
      confirm: (message) => window.confirm(message),
    }));
  }

  if (locations.isPending) return <Spinner />;
  if (locations.isError) {
    return (
      <div className="master-data-panel">
        <ErrorNotice error={locations.error} />
        <button type="button" onClick={() => void locations.refetch()}>{t('blocking.retry')}</button>
      </div>
    );
  }

  return (
    <div className="master-data-panel master-data-panel-locations">
      <h2>{t('masterData.locations.title')}</h2>
      <PanelError message={shell.error} />

      {locations.data.locations.length === 0 ? (
        <p>{t('masterData.locations.empty')}</p>
      ) : (
        <table className="locations-table">
          <thead>
            <tr>
              <th>{t('masterData.locations.col.name')}</th>
              <th>{t('masterData.locations.col.barcode')}</th>
              <th>{t('masterData.locations.col.updatedAt')}</th>
              <th>{t('masterData.locations.col.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {locations.data.locations.map((row) => (
              <tr key={row.id}>
                <td>{row.name}</td>
                <td>{locationBarcodeLabel(row)}</td>
                <td>{formatDateTime(row.updatedAt)}</td>
                <td>
                  <button type="button" disabled={shell.busy} onClick={() => openRename(row)}>
                    {t('masterData.locations.rename')}
                  </button>
                  <button type="button" disabled={shell.busy} onClick={() => openBarcodeDialog(row)}>
                    {t('masterData.locations.setBarcode')}
                  </button>
                  {row.barcode === null ? null : (
                    <button type="button" disabled={shell.busy} onClick={() => handleClearBarcode(row)}>
                      {t('masterData.locations.clearBarcode')}
                    </button>
                  )}
                  <button type="button" disabled={shell.busy} onClick={() => handleDelete(row)}>
                    {t('masterData.locations.delete')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {renameTarget === null ? null : (
        <form
          className="location-rename-dialog"
          role="dialog"
          aria-label={t('masterData.locations.renameDialog.title', { name: renameTarget.name })}
          onSubmit={handleRenameSubmit}
        >
          <h3>{t('masterData.locations.renameDialog.title', { name: renameTarget.name })}</h3>
          <label>
            {t('masterData.locations.renameDialog.label')}
            <input
              type="text"
              required
              value={renameValue}
              disabled={shell.busy}
              onChange={(event) => setRenameValue(event.target.value)}
            />
          </label>
          <button type="submit" disabled={shell.busy}>{t('masterData.locations.renameDialog.submit')}</button>
          <button type="button" onClick={() => { setRenameTarget(null); setRenameValue(''); }}>
            {t('masterData.locations.renameDialog.cancel')}
          </button>
        </form>
      )}

      {barcodeTarget === null ? null : (
        <form
          className="location-barcode-dialog"
          role="dialog"
          aria-label={t('masterData.locations.barcodeDialog.title', { name: barcodeTarget.name })}
          onSubmit={handleBarcodeSubmit}
        >
          <h3>{t('masterData.locations.barcodeDialog.title', { name: barcodeTarget.name })}</h3>
          <label>
            {t('masterData.locations.barcodeDialog.label')}
            <input
              type="text"
              required
              value={barcodeValue}
              disabled={shell.busy}
              onChange={(event) => setBarcodeValue(event.target.value)}
            />
          </label>
          <button type="submit" disabled={shell.busy}>{t('masterData.locations.barcodeDialog.submit')}</button>
          <button type="button" onClick={() => { setBarcodeTarget(null); setBarcodeValue(''); }}>
            {t('masterData.locations.barcodeDialog.cancel')}
          </button>
        </form>
      )}

      <form className="location-create" onSubmit={handleCreate}>
        <h3>{t('masterData.locations.create.title')}</h3>
        <label>
          {t('masterData.locations.create.name')}
          <input
            type="text"
            required
            value={name}
            disabled={shell.busy}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <label>
          {t('masterData.locations.create.barcode')}
          <input
            type="text"
            value={barcode}
            disabled={shell.busy}
            onChange={(event) => setBarcode(event.target.value)}
          />
        </label>
        <button type="submit" disabled={shell.busy}>{t('masterData.locations.create.submit')}</button>
      </form>
    </div>
  );
}

// ── Listen ───────────────────────────────────────────────────────────────────

function ListsPanel() {
  const shell = useMutationShell();
  const lists = useQuery(listsQueryOptions());
  const [name, setName] = useState('');
  const [renameTarget, setRenameTarget] = useState<ListEntry | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  // Reorder has no `shell.busy` of its own -- it is the one optimistic
  // mutation on this view (lib/masterDataView.ts's `reorderListsAction`
  // docblock): the row moves in the cache before the round trip, which is
  // the whole point of not blocking the UI while it is in flight.
  const [reorderError, setReorderError] = useState<string | null>(null);

  function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    guardedAction(shell.gate, 'createList', () => createListAction(
      name, { ...shell, createList: ADMIN_API.createList, onCreated: () => setName('') },
    ));
  }

  function openRename(row: ListEntry) {
    setRenameTarget(row);
    setRenameValue(row.name);
    shell.setError(null);
  }
  function handleRenameSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (renameTarget === null) return;
    const target = renameTarget;
    guardedAction(shell.gate, 'renameList', () => renameListAction(target, renameValue, {
      ...shell,
      renameList: ADMIN_API.renameList,
      onRenamed: () => { setRenameTarget(null); setRenameValue(''); },
    }));
  }

  function handleDelete(row: ListEntry) {
    guardedAction(shell.gate, 'deleteList', () => deleteListAction(row, {
      ...shell,
      deleteList: ADMIN_API.deleteList,
      confirm: (message) => window.confirm(message),
    }));
  }

  function handleDragStart(index: number) {
    setDragIndex(index);
  }
  function handleDragOver(event: DragEvent<HTMLTableRowElement>) {
    event.preventDefault(); // permits a drop; without this, onDrop never fires
  }
  /** The row a drag was released ON is the `to` -- `rows` is passed in from
   *  the render that created this closure, the SAME array `reorderListsAction`
   *  computes `moveItem` against, never re-read from a ref that could have
   *  drifted. */
  function handleDrop(to: number, rows: readonly ListEntry[]) {
    const from = dragIndex;
    setDragIndex(null);
    if (from === null) return;
    void reorderListsAction(rows, from, to, {
      queryClient: shell.queryClient,
      reorderLists: ADMIN_API.reorderLists,
      setError: setReorderError,
    });
  }

  if (lists.isPending) return <Spinner />;
  if (lists.isError) {
    return (
      <div className="master-data-panel">
        <ErrorNotice error={lists.error} />
        <button type="button" onClick={() => void lists.refetch()}>{t('blocking.retry')}</button>
      </div>
    );
  }

  const rows = lists.data.lists;

  return (
    <div className="master-data-panel master-data-panel-lists">
      <h2>{t('masterData.lists.title')}</h2>
      <PanelError message={shell.error} />
      <PanelError message={reorderError} />
      <p className="lists-rename-note">{t('masterData.lists.renameNote')}</p>

      {rows.length === 0 ? (
        <p>{t('masterData.lists.empty')}</p>
      ) : (
        <table className="lists-table">
          <thead>
            <tr>
              <th aria-hidden="true" />
              <th>{t('masterData.lists.col.name')}</th>
              <th>{t('masterData.lists.col.updatedAt')}</th>
              <th>{t('masterData.lists.col.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr
                key={row.id}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={handleDragOver}
                onDrop={() => handleDrop(index, rows)}
              >
                <td className="drag-handle" aria-label={t('masterData.lists.dragHandle')}>⠿</td>
                <td>{row.name}</td>
                <td>{formatDateTime(row.updatedAt)}</td>
                <td>
                  <button type="button" disabled={shell.busy} onClick={() => openRename(row)}>
                    {t('masterData.lists.rename')}
                  </button>
                  <button type="button" disabled={shell.busy} onClick={() => handleDelete(row)}>
                    {t('masterData.lists.delete')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {renameTarget === null ? null : (
        <form
          className="list-rename-dialog"
          role="dialog"
          aria-label={t('masterData.lists.renameDialog.title', { name: renameTarget.name })}
          onSubmit={handleRenameSubmit}
        >
          <h3>{t('masterData.lists.renameDialog.title', { name: renameTarget.name })}</h3>
          <label>
            {t('masterData.lists.renameDialog.label')}
            <input
              type="text"
              required
              value={renameValue}
              disabled={shell.busy}
              onChange={(event) => setRenameValue(event.target.value)}
            />
          </label>
          <button type="submit" disabled={shell.busy}>{t('masterData.lists.renameDialog.submit')}</button>
          <button type="button" onClick={() => { setRenameTarget(null); setRenameValue(''); }}>
            {t('masterData.lists.renameDialog.cancel')}
          </button>
        </form>
      )}

      <form className="list-create" onSubmit={handleCreate}>
        <h3>{t('masterData.lists.create.title')}</h3>
        <label>
          {t('masterData.lists.create.name')}
          <input
            type="text"
            required
            value={name}
            disabled={shell.busy}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <button type="submit" disabled={shell.busy}>{t('masterData.lists.create.submit')}</button>
      </form>
    </div>
  );
}

// ── Einstellungen ────────────────────────────────────────────────────────────

/**
 * `key={settings.dataUpdatedAt}` at the call site remounts this whenever the
 * query's data actually changes (including the refetch a successful save
 * triggers via `INVALIDATES.putSettings`), so the draft never lags a write
 * this same form just made. `initial` is a snapshot, never re-read live: the
 * form's local `original`/`draft` pair is what `changedSettings` diffs
 * against, not the query cache directly.
 */
function SettingsForm({ initial }: { initial: SettingsResponse }) {
  const shell = useMutationShell();
  const [original, setOriginal] = useState(initial.settings);
  const [draft, setDraft] = useState(initial.settings);
  const [saved, setSaved] = useState(false);
  const [noChanges, setNoChanges] = useState(false);

  function handleChange(key: string, value: string) {
    setSaved(false);
    setNoChanges(false);
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaved(false);
    // Checked HERE, with the same pure `changedSettings` diff
    // `saveSettingsAction` itself uses, so "nothing to save" is immediate
    // feedback rather than a silent no-op indistinguishable from a slow save.
    if (Object.keys(changedSettings(original, draft)).length === 0) {
      setNoChanges(true);
      return;
    }
    setNoChanges(false);
    guardedAction(shell.gate, 'putSettings', () => saveSettingsAction(original, draft, {
      ...shell,
      putSettings: ADMIN_API.putSettings,
      onSaved: (response) => {
        setOriginal(response.settings);
        setDraft(response.settings);
        setSaved(true);
      },
    }));
  }

  const groups = settingsGroups(initial.keys);

  return (
    <form className="settings-form" onSubmit={handleSubmit}>
      <PanelError message={shell.error} />
      {saved ? <p role="status">{t('masterData.settings.saved')}</p> : null}
      {noChanges ? <p role="status">{t('masterData.settings.noChanges')}</p> : null}

      <fieldset>
        <legend>{t('masterData.settings.company.title')}</legend>
        {groups.company.map((key) => (
          <label key={key}>
            {settingsFieldLabel(key)}
            <input
              type="text"
              value={draft[key] ?? ''}
              placeholder={settingsFieldPlaceholder(key)}
              disabled={shell.busy}
              onChange={(event) => handleChange(key, event.target.value)}
            />
          </label>
        ))}
      </fieldset>

      <fieldset className="settings-datev">
        <legend>{t('masterData.settings.datev.title')}</legend>
        <p>{t('masterData.settings.datev.note')}</p>
        {groups.datev.map((key) => (
          <label key={key}>
            {settingsFieldLabel(key)}
            <input
              type="text"
              value={draft[key] ?? ''}
              disabled={shell.busy}
              onChange={(event) => handleChange(key, event.target.value)}
            />
          </label>
        ))}
      </fieldset>

      <button type="submit" disabled={shell.busy}>{t('masterData.settings.save')}</button>
    </form>
  );
}

/**
 * D10: `PATCH /admin-api/features` accepts exactly `{ ebay?, priceScan? }`.
 * `photos`/`enrichment` render read-only WITH an explanation, never as
 * toggles: the merged handler refuses them with `400 not_self_serve` rather
 * than ignoring them (task-10-brief.md Step 4), so a toggle for either would
 * be a guaranteed error, not a degraded control.
 */
function FeaturesSection({ features }: { features: Features }) {
  const shell = useMutationShell();
  const { navigate } = useRoute();

  function handleToggle(flag: SelfServeFeature, next: boolean) {
    guardedAction(shell.gate, 'patchFeatures', () => toggleSelfServeFeatureAction(
      flag, next, { ...shell, patchFeatures: ADMIN_API.patchFeatures },
    ));
  }

  function handleEbayLinkClick(event: MouseEvent<HTMLAnchorElement>) {
    if (!isPlainLeftClick(event)) return;
    event.preventDefault();
    navigate(pathFor('ebay'));
  }

  return (
    <fieldset className="master-data-features">
      <legend>{t('masterData.features.title')}</legend>
      <PanelError message={shell.error} />

      <label>
        <input
          type="checkbox"
          checked={features.ebay}
          disabled={shell.busy}
          onChange={(event) => handleToggle('ebay', event.target.checked)}
        />
        {t('masterData.features.ebay.label')}
      </label>
      {/* Turning this OFF is not a disconnect: the connection row survives
          (task-10-brief.md Step 4). The real disconnect lives at /ebay. */}
      <p>
        {t('masterData.features.ebay.hint')}
        {' '}
        <a href={pathFor('ebay')} onClick={handleEbayLinkClick}>{t('nav.ebay')}</a>
      </p>

      <label>
        <input
          type="checkbox"
          checked={features.priceScan}
          disabled={shell.busy}
          onChange={(event) => handleToggle('priceScan', event.target.checked)}
        />
        {t('masterData.features.priceScan.label')}
      </label>

      <div className="feature-readonly">
        <span>{t('masterData.features.photos.label')}</span>
        <StatusPill
          kind={features.photos ? 'ok' : 'neutral'}
          label={features.photos ? t('masterData.features.on') : t('masterData.features.off')}
        />
        <p>{t('masterData.features.operationsNote')}</p>
      </div>
      <div className="feature-readonly">
        <span>{t('masterData.features.enrichment.label')}</span>
        <StatusPill
          kind={features.enrichment ? 'ok' : 'neutral'}
          label={features.enrichment ? t('masterData.features.on') : t('masterData.features.off')}
        />
        <p>{t('masterData.features.operationsNote')}</p>
      </div>
    </fieldset>
  );
}

function SettingsPanel({ features }: { features: Features }) {
  const settings = useQuery(settingsQueryOptions());

  if (settings.isPending) return <Spinner />;
  if (settings.isError) {
    return (
      <div className="master-data-panel">
        <ErrorNotice error={settings.error} />
        <button type="button" onClick={() => void settings.refetch()}>{t('blocking.retry')}</button>
      </div>
    );
  }

  return (
    <div className="master-data-panel master-data-panel-settings">
      <h2>{t('masterData.settings.title')}</h2>
      <SettingsForm key={settings.dataUpdatedAt} initial={settings.data} />
      <FeaturesSection features={features} />
    </div>
  );
}

// ── the view ─────────────────────────────────────────────────────────────────

export function MasterData() {
  const me = useMe();
  const { hash, navigate } = useRoute();

  // Boot resolves `me` before any view mounts, so this is a cache read (the
  // same guard views/Staff.tsx's `Staff()` uses).
  if (!me.isSuccess) return <Spinner />;

  const tab = parseMasterDataTab(hash);

  function handleTabClick(event: MouseEvent<HTMLAnchorElement>, href: string) {
    if (!isPlainLeftClick(event)) return;
    event.preventDefault();
    navigate(href);
  }

  return (
    <section className="master-data-view">
      <PageBody>
        <PageHeader title={t('nav.masterData')} />
        <TabNav
          className="master-data-tabs"
          label={t('masterData.tabs.label')}
          activeId={tab}
          items={MASTER_DATA_TABS.map((id) => ({
            id,
            label: masterDataTabLabel(id),
            href: masterDataTabHref(id),
          }))}
          onTabClick={handleTabClick}
        />

        {tab === 'locations' ? <LocationsPanel /> : null}
        {tab === 'lists' ? <ListsPanel /> : null}
        {tab === 'settings' ? <SettingsPanel features={me.data.features} /> : null}
      </PageBody>
    </section>
  );
}
